/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1669593600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Mon Nov 28 2022 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "mattermost",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Setup",
          "depth": 0,
          "id": "setup"
        }, {
          "value": "Sending Mattermost messages (added / changed / removed)",
          "depth": 0,
          "id": "sending-mattermost-messages-added--changed--removed"
        }, {
          "value": "Channel selection",
          "depth": 1,
          "id": "channel-selection"
        }, {
          "value": "Added, changed, and removed messages",
          "depth": 1,
          "id": "added-changed-and-removed-messages"
        }, {
          "value": "Sending Mattermost messages (all results)",
          "depth": 0,
          "id": "sending-mattermost-messages-all-results"
        }, {
          "value": "Channel selection",
          "depth": 1,
          "id": "channel-selection-1"
        }, {
          "value": "Header, body, and footer content",
          "depth": 1,
          "id": "header-body-and-footer-content"
        }, {
          "value": "Templating messages",
          "depth": 0,
          "id": "templating-messages"
        }, {
          "value": "Liquid templating",
          "depth": 1,
          "id": "liquid-templating"
        }, {
          "value": "Tagging Mattermost users (@...)",
          "depth": 1,
          "id": "tagging-mattermost-users-"
        }, {
          "value": "Unfurling links and blocking unfurl",
          "depth": 1,
          "id": "unfurling-links-and-blocking-unfurl"
        }, {
          "value": "Table",
          "depth": 0,
          "id": "table"
        }, {
          "value": "Channel selection",
          "depth": 1,
          "id": "channel-selection-2"
        }, {
          "value": "Using webhooks",
          "depth": 0,
          "id": "using-webhooks"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      ol: "ol",
      li: "li",
      img: "img",
      h3: "h3",
      ul: "ul",
      a: "a",
      strong: "strong",
      pre: "pre",
      code: "code",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "With the Mattermost destination, Hightouch can serve as a notifier for various changes. This is a very flexible integration, but here are a few example use cases:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Notify your customer success teams when product usage for a key account suddenly drops"
        }), "\n", _jsx(_components.li, {
          children: "Notify an account executive when product usage surpasses a certain threshold"
        }), "\n", _jsx(_components.li, {
          children: "Send a daily summary of the number of sign ups, compared to the same day last month"
        }), "\n", _jsx(_components.li, {
          children: "Send a daily summary on the adoption of a core feature"
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "setup",
        children: "Setup"
      }), "\n", _jsx(_components.p, {
        children: "Use personal access tokens to connect to Mattermost. Check with your Mattermost administrator that personal access tokens are enabled."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["In Mattermost, click your icon in the top right and go to Profile to access your personal settings\n", _jsx(_components.img, {
            src: "destinations/destination-mattermost-user-settings.png",
            alt: ""
          })]
        }), "\n", _jsx(_components.li, {
          children: "Click the Security tab in the modal that appears"
        }), "\n", _jsxs(_components.li, {
          children: ["Click Personal Access Tokens and create the token\n", _jsx(_components.img, {
            src: "destinations/destination-mattermost-security-settings.png",
            alt: ""
          })]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "sending-mattermost-messages-added--changed--removed",
        children: "Sending Mattermost messages (added / changed / removed)"
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Hightouch will not send any messages on the initial run to prevent a flood of\nmessages to your channel. Messages will start after the this initial sync."
        })
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports sending messages based on rows that have been added, changed, or removed since the last sync run. Each row is sent as an individual message and can be sent to different channels and templated with Liquid."
      }), "\n", _jsx(_components.p, {
        children: "Message formatting is best when the following conditions are met:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "You want to write a query, and have Hightouch detect the added and removed rows"
        }), "\n", _jsx(_components.li, {
          children: "You want a custom message sent per each added and removed rows"
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "channel-selection",
        children: "Channel selection"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports both sending messages to a specific channel that the Hightouch bot is part of, or by pulling the channel ID each row. Enable the \"Use column\" toggle to dynamically set the target channel."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-mattermost-channel-selection.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "added-changed-and-removed-messages",
        children: "Added, changed, and removed messages"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch allows you to template the content you would like to send in your message."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "When a row is added in your query results, Hightouch will send the row added content."
        }), "\n", _jsx(_components.li, {
          children: "When a row is changed in your query results, Hightouch will send the row changed content."
        }), "\n", _jsx(_components.li, {
          children: "When a row is deleted in your query results, Hightouch will send the row deleted content."
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["This content can be ", _jsx(_components.a, {
          href: "#liquid-templating",
          children: "templated"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-mattermost-content.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h2, {
        id: "sending-mattermost-messages-all-results",
        children: "Sending Mattermost messages (all results)"
      }), "\n", _jsx(_components.p, {
        children: "The batch message mode is similar to the normal message mode, except it sends all rows in the query results rather than only added / changed / removed rows since the last sync."
      }), "\n", _jsx(_components.p, {
        children: "This mode also allows you to specify how many rows are sent per message, which can range from 1 to 20. With 1 row per message, it would be similar to the message mode, whereas 20 rows per message would be similar to a tabular representation."
      }), "\n", _jsx(_components.p, {
        children: "This format is good for the following scenarios."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["You want to write a query, and have Hightouch push a message containing ", _jsx(_components.strong, {
            children: "all"
          }), " rows, formatted as a message, to Mattermost"]
        }), "\n", _jsx(_components.li, {
          children: "You don't want Hightouch to detect added and removed rows, but instead want to send all rows on each query run"
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "channel-selection-1",
        children: "Channel selection"
      }), "\n", _jsx(_components.p, {
        children: "The batch message only supports sending your messages to a single channel."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-mattermost-channel-updates.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "header-body-and-footer-content",
        children: "Header, body, and footer content"
      }), "\n", _jsx(_components.p, {
        children: "For each message, Hightouch allows setting the header, footer, and row content. Only the row content is required. The row content is repeated per row in the message and has access to the row values via templating."
      }), "\n", _jsxs(_components.p, {
        children: ["This content can be ", _jsx(_components.a, {
          href: "#liquid-templating",
          children: "templated"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-mattermost-batch-content.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h2, {
        id: "templating-messages",
        children: "Templating messages"
      }), "\n", _jsx(_components.h3, {
        id: "liquid-templating",
        children: "Liquid templating"
      }), "\n", _jsxs(_components.p, {
        children: ["For both the message and batch message modes, ", _jsx(_components.a, {
          href: "https://shopify.github.io/liquid/",
          children: "Liquid templating "
        }), "is supported. The most important implication of this is that columns from the added or removed row can be accessed as part of the message body. Here is an example message making use of Liquid to insert a column into the message."]
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-text",
          children: "User with name {{ row[\"name\"] }} and email {{ row[\"email\"] }} has signed up\n"
        })
      }), "\n", _jsx(_components.h3, {
        id: "tagging-mattermost-users-",
        children: "Tagging Mattermost users (@...)"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports tagging users by either their Mattermost username (if you have it in your warehouse) or by email."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Tagging by username:"
          }), " tag by username by writing ", _jsx(_components.code, {
            children: "<@{{ row[\"username\"] }}>"
          }), " where username is the column that contains the user's Mattermost username, just as using Liquid templating. If the username is a static value, you can write something like ", _jsx(_components.code, {
            children: "<@exampleuser>"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Tagging by Email:"
          }), " tag by email by writing ", _jsx(_components.code, {
            children: "<@{{ row[\"email\"] | mattermost_user_lookup }}>"
          }), " where email is the column that contains the user's email used in Mattermost. If the email is a static value, you can write something like ", _jsx(_components.code, {
            children: "<@{{ \"example@gmail.com\" | mattermost_user_lookup }}>"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: "To tag by email, we require the permission users:read.email."
      }), "\n", _jsx(_components.h3, {
        id: "unfurling-links-and-blocking-unfurl",
        children: "Unfurling links and blocking unfurl"
      }), "\n", _jsx(_components.p, {
        children: "By default, Mattermost automatically unfurls (displays a preview) URL links in your messages. To prevent automatic unfurling, surround the URL with backticks, for example:"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.code, {
          children: "https://www.hightouch.com/"
        })
      }), "\n", _jsx(_components.p, {
        children: "(Note: The backtick is usually located above the tab key on the left-hand side of the keyboard)."
      }), "\n", _jsx(_components.h2, {
        id: "table",
        children: "Table"
      }), "\n", _jsx(_components.p, {
        children: "Table formatting sends all of your query results formatted as a text table as messages to Mattermost. This mode is recommended only for small sets of data."
      }), "\n", _jsx(_components.p, {
        children: "This format is good for the following scenarios."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["You want to write a query, and have Hightouch push a message containing ", _jsx(_components.strong, {
            children: "all"
          }), " rows, formatted as a table, to Mattermost"]
        }), "\n", _jsx(_components.li, {
          children: "You don't want Hightouch to detect added and removed rows, but instead want to send all rows on each query run"
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "channel-selection-2",
        children: "Channel selection"
      }), "\n", _jsx(_components.p, {
        children: "The batch message only supports sending your messages to a single channel."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-mattermost-channel-updates.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h2, {
        id: "using-webhooks",
        children: "Using webhooks"
      }), "\n", _jsx(_components.p, {
        children: "If you enable the option to use webhooks for sending messages, ensure the user whose access token was used has permissions to create and get webhooks. Using webhooks allows you to set how the sender is displayed when sending the message. This can be configured with the following fields"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              align: "left",
              children: "Name"
            }), _jsx(_components.th, {
              align: "left",
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: "Icon Image URL"
            }), _jsx(_components.td, {
              align: "left",
              children: "URL of 48x48 image of the icon that you wish to use"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: "Icon Emoji"
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Mattermost emoji (for example, ", _jsx(_components.code, {
                children: ":sweat_smile:"
              }), "), this will overwrite icon image URL"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: "Bot Name"
            }), _jsx(_components.td, {
              align: "left",
              children: "Display name of bot"
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "(Note: Using webhooks for the CSV mode is not supported since files can't be uploaded using webhooks)"
      })]
    });
  }
}
export default MDXContent;
